<script setup lang="ts">
import { computed, watch, onMounted } from 'vue'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import { SET_FORM_SUBMITTED_STATUS } from '@/store/mutation-types'
import EmailForm from '@/components/views/manage-account/Login/EmailForm.vue'
import { LoginMethod } from '@/types'

const store = useStore()
const route = useRoute()
const router = useRouter()

const isSignup = computed(() => store.state.auth.authIntent === 'signup')
const loginMethod = computed(() => store.state.auth.loginMethod)
const isMagicLink = computed(() => loginMethod.value === LoginMethod.LINK)

const header = computed(() => {
  if (isSignup.value) {
    return isMagicLink.value
      ? 'ManageAccount.Signup.Start.MagicLinkHeader'
      : 'ManageAccount.Signup.Start.CodeHeader'
  }
  return isMagicLink.value
    ? 'ManageAccount.Login.Start.MagicLinkHeader'
    : 'ManageAccount.Login.Start.CodeHeader'
})

const para = computed(() => {
  if (isSignup.value) {
    return isMagicLink.value
      ? 'ManageAccount.Signup.Start.MagicLinkParagraph'
      : 'ManageAccount.Signup.Start.CodeParagraph'
  }
  return isMagicLink.value
    ? 'ManageAccount.Login.Start.MagicLinkParagraph'
    : 'ManageAccount.Login.Start.CodeParagraph'
})

const switchMethodLinkText = computed(() =>
  isMagicLink.value
    ? 'ManageAccount.Login.Start.SwitchToCode'
    : 'ManageAccount.Login.Start.SwitchToMagicLink'
)

/** Navigate to the alternate method URL so support can deep-link magic link. */
const switchLoginMethod = () => {
  store.commit(SET_FORM_SUBMITTED_STATUS, false)
  if (isSignup.value) {
    router.push({ name: isMagicLink.value ? 'Signup' : 'SignupMagicLink' })
    return
  }
  router.push({ name: isMagicLink.value ? 'Login' : 'LoginMagicLink' })
}

onMounted(() => {
  // Fresh start each time the auth screen mounts.
  store.commit(SET_FORM_SUBMITTED_STATUS, false)
})

watch(
  () => route?.name,
  () => {
    store.commit(SET_FORM_SUBMITTED_STATUS, false)
  }
)
</script>

<template>
  <div class="sa-auth-step">
    <header class="sa-auth-step__header">
      <p v-if="isSignup" class="sa-auth-step__eyebrow">
        {{ $t('ManageAccount.Signup.Start.Eyebrow') }}
      </p>
      <h1 class="sa-auth-step__title">{{ $t(header) }}</h1>
      <p class="sa-auth-step__lead">{{ $t(para) }}</p>
    </header>

    <EmailForm :login-method="loginMethod" />

    <p class="sa-auth__alt">
      {{ $t('ManageAccount.Login.Start.TroubleSigningIn') }}
      <button
        type="button"
        class="sa-auth__alt-btn"
        data-testid="login-link-method"
        @click.prevent="switchLoginMethod"
      >
        {{ $t(switchMethodLinkText) }}
      </button>
    </p>

    <p v-if="!isSignup" class="sa-auth__signup">
      {{ $t('LoginRegister.Email.NoSwiftaidAccount') }}
      <router-link class="sa-link" :to="{ name: 'Signup' }" data-testid="signup-link">
        {{ $t('LoginRegister.Email.SignUpHere') }}
      </router-link>
    </p>

    <p v-else class="sa-auth__signup">
      {{ $t('ManageAccount.Signup.Start.HaveAccount') }}
      <router-link class="sa-link" :to="{ name: 'Login' }" data-testid="login-link">
        {{ $t('ManageAccount.Signup.Start.SignInHere') }}
      </router-link>
    </p>
  </div>
</template>
